import { useCallback } from 'react';

import { useQueryClient } from '@lumapps/base-api/react-query';
import { get } from '@lumapps/constants';
import { useDispatch, useSelector } from '@lumapps/redux/react';
import { AppId } from '@lumapps/router';
import { inputLanguageSelector } from '@lumapps/translations/ducks/selectors';

import { queryKeys } from '../api';
import { useOwnerResourceInfo } from '../components/Widget/useOwnerResourceInfo';
import { useWidgetState } from '../components/Widget/useWidgetState';
import { actions } from '../ducks/slice';
import { LegacyWidget, LegacyWidgetProperties } from '../types';
import { getEffectiveWidgetType } from '../utils/getEffectiveWidgetType';
import { getWidgetProperties } from '../utils/getWidgetProperties';

const constant = get();
const isLegacyApp = constant.applicationId === AppId.legacy;

export const useSetClientComputedEmpty = <WP = LegacyWidgetProperties>(
    widgetId: string,
    excludedQueryKeyWidgetProperties: Array<keyof WP> = [],
) => {
    const { ownerResourceId = '', ownerResourceType = '' } = useOwnerResourceInfo() ?? {};

    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const inputLanguage = useSelector(inputLanguageSelector);

    const widgetState = useWidgetState(widgetId);

    const effectiveWidgetType = getEffectiveWidgetType(widgetState);

    return useCallback(
        (widgetProperties?: WP) => {
            if (isLegacyApp) {
                dispatch(actions.setWidgetProperties({ widgetId, widgetProperties: { state: 'empty' } }));
            } else {
                // Also update React Query cache for immediate feedback
                // In V1 format, the legacyWidget is defined and we are fetching the block
                const isWidgetV1 = !!widgetState.legacyWidget;
                const queryKey = queryKeys.getWidgetBlocks({
                    ownerResourceId,
                    ownerResourceType,
                    widgetType: effectiveWidgetType,
                    widgetId,
                    language: inputLanguage,
                    // Use full legacyWidget (includes all properties) for exact queryKey match
                    // This prevents polluting cache entries with different properties
                    properties: isWidgetV1
                        ? getWidgetProperties<WP>(
                              { ...widgetState.legacyWidget, properties: widgetProperties } as LegacyWidget<WP>,
                              excludedQueryKeyWidgetProperties,
                          )
                        : undefined,
                });

                queryClient.setQueryData(queryKey, (old: any) => {
                    if (!old?.pages?.[0]) {
                        return undefined;
                    }

                    return {
                        ...old,
                        pages: [{ ...old?.pages[0], isClientComputedEmpty: true }],
                    };
                });
            }
        },
        [
            dispatch,
            effectiveWidgetType,
            excludedQueryKeyWidgetProperties,
            inputLanguage,
            ownerResourceId,
            ownerResourceType,
            queryClient,
            widgetId,
            widgetState.legacyWidget,
        ],
    );
};
